/**
 * Session-scoped accumulator for the save-time override classifier's
 * inputs (per-interviewer working hours + busy events).
 *
 * Why this exists (ENG-3571): `useCommonAvailabilityRange` fetches a
 * fixed 4-week window anchored on the **currently visible** week, and
 * `useBusyCalendarEvents` clamps busy events to the same span. Both
 * windows move forward as the user navigates, so a slot picked in an
 * earlier week has no working-hours row and no busy event by the time
 * Save is pressed. `computeSlotOverride` then reads "no interval
 * contains this slot" as "outside working hours" and ships
 * `override_type: 'working_hours'` — which leaves the backend's
 * calendar filter armed, so a slot the recruiter deliberately placed
 * on a busy event silently never reaches the candidate.
 *
 * Legacy `KInterviewScheduler` never hit this: its `userEventsInfo`
 * store accumulated every week the user had visited. This restores
 * that property for the wizard — the user must have had a week on
 * screen to pick in it, so its data has already been fetched; we just
 * stop throwing it away.
 *
 * `coveredRanges` is the other half: it records the spans we actually
 * hold data for, so the classifier can tell "in hours" apart from
 * "never fetched" instead of guessing. Weeks whose fetch failed are
 * simply never added.
 *
 * Callers must drop the accumulation when the interviewer roster or
 * the timezone changes — see `CalendarPanel`'s reset key. Stale busy
 * events are acceptable (they can only widen an override, and the
 * backend re-validates at booking time); another interviewer's hours
 * are not.
 */
import type { BusyEvent, Interval } from '../../availability';

export interface ScheduleContextSnapshot {
  workingHoursByInterviewer: Record<string, Interval[]>;
  busyEvents: BusyEvent[];
  /**
   * Windows the accumulation actually covers. A slot outside every
   * range can't be classified and must not be given an override.
   */
  coveredRanges: Interval[];
}

export interface ScheduleContextWeek {
  /** Week window this data covers. */
  startIso: string;
  endIso: string;
  /** Raw per-interviewer working hours for the week. */
  scheduleByUserId: Record<string, Interval[]>;
}

export interface ScheduleContextChunk {
  /** Only weeks whose fetch RESOLVED — never include errored/pending ones. */
  weeks: ReadonlyArray<ScheduleContextWeek>;
  busyEvents: ReadonlyArray<BusyEvent>;
}

export const EMPTY_SCHEDULE_CONTEXT: ScheduleContextSnapshot = {
  workingHoursByInterviewer: {},
  busyEvents: [],
  coveredRanges: [],
};

const intervalKey = (iv: Interval) => `${iv.start}|${iv.end}`;
const busyKey = (evt: BusyEvent) => `${evt.id}|${evt.start}|${evt.end}`;

/**
 * Union of `[start, end)` spans, coalescing overlapping and touching
 * ones so a slot straddling two consecutive fetched weeks still reads
 * as covered. Unparseable spans are dropped.
 */
function coalesce(ranges: Interval[]): Interval[] {
  const parsed = ranges
    .map((r) => ({
      raw: r,
      start: Date.parse(r.start),
      end: Date.parse(r.end),
    }))
    .filter(
      (r) =>
        Number.isFinite(r.start) && Number.isFinite(r.end) && r.end > r.start
    )
    .sort((a, b) => a.start - b.start);
  const out: Array<{ raw: Interval; start: number; end: number }> = [];
  for (const cur of parsed) {
    const last = out[out.length - 1];
    if (last && cur.start <= last.end) {
      if (cur.end > last.end) {
        last.end = cur.end;
        last.raw = { start: last.raw.start, end: cur.raw.end };
      }
      continue;
    }
    out.push({ ...cur });
  }
  return out.map((r) => r.raw);
}

/**
 * Fold a freshly-settled window into the running snapshot. Returns
 * `prev` by identity when nothing new arrived, so the caller's
 * change-detection stays cheap.
 */
export function accumulateScheduleContext(
  prev: ScheduleContextSnapshot,
  chunk: ScheduleContextChunk
): ScheduleContextSnapshot {
  let changed = false;

  const workingHours: Record<string, Interval[]> = {};
  const seenByInterviewer: Record<string, Set<string>> = {};
  for (const [id, intervals] of Object.entries(
    prev.workingHoursByInterviewer
  )) {
    workingHours[id] = [...intervals];
    seenByInterviewer[id] = new Set(intervals.map(intervalKey));
  }
  for (const week of chunk.weeks) {
    for (const [id, intervals] of Object.entries(week.scheduleByUserId)) {
      if (!intervals || intervals.length === 0) continue;
      const seen = (seenByInterviewer[id] ??= new Set());
      const bucket = (workingHours[id] ??= []);
      for (const iv of intervals) {
        const key = intervalKey(iv);
        if (seen.has(key)) continue;
        seen.add(key);
        bucket.push(iv);
        changed = true;
      }
    }
  }

  const busyEvents = [...prev.busyEvents];
  const seenBusy = new Set(busyEvents.map(busyKey));
  for (const evt of chunk.busyEvents) {
    const key = busyKey(evt);
    if (seenBusy.has(key)) continue;
    seenBusy.add(key);
    busyEvents.push(evt);
    changed = true;
  }

  const seenRanges = new Set(prev.coveredRanges.map(intervalKey));
  const nextRanges = [...prev.coveredRanges];
  for (const week of chunk.weeks) {
    const range = { start: week.startIso, end: week.endIso };
    if (seenRanges.has(intervalKey(range))) continue;
    seenRanges.add(intervalKey(range));
    nextRanges.push(range);
    changed = true;
  }

  if (!changed) return prev;

  return {
    workingHoursByInterviewer: workingHours,
    busyEvents,
    coveredRanges: coalesce(nextRanges),
  };
}
